<template>
    <div class="campaign-builder-modal">
        <GoalModal
            data-qa="failed-purchase-modal-goal"
            :is-open="isOpen"
            :goal-tip-link="toolTipLink"
            :goal-tip-text="$t('funnel.edit.sideBar.goal.failedPurchase.proTip')"
            :summary-text="$t('funnel.edit.sideBar.goal.failedPurchase.summary')"
            :title="$t('funnel.edit.sideBar.goal.failedPurchase')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #goalParameters>
                <section class="purchase-type">
                    <DsMultiselect
                        v-model="selectedFailedPurchaseType"
                        data-qa="failed-purchase-type"
                        bind-value-only
                        label-prop="label"
                        value-prop="value"
                        class="selected-purchase-type"
                        :placeholder="$t('campaign.product.purchased.purchaseTypePlaceholder')"
                        :options="failedPurchaseTypes"
                        @input="selectPaymentOption"
                    >
                        <template #option="{ option, highlight, dense, isSelected }">
                            <div
                                :data-qa="option.value"
                                class="menu-item"
                                :class="[{
                                    highlight,
                                    dense,
                                    selected: isSelected
                                }]"
                            >
                                <div>
                                    <span>{{ option.label }}</span>
                                    <br />
                                    <span v-if="option.helpText" class="option-subtext">{{ option.helpText }}</span>
                                </div>
                            </div>
                        </template>
                    </DsMultiselect>
                </section>
                <CustomMultiselect
                    v-if="isProduct"
                    v-model="selectedProduct"
                    bind-value-only
                    categorized
                    custom-chip-label
                    searchable
                    label-prop="chipLabel"
                    value-prop="value"
                    help-text
                    help-text-prop="subText"
                    :force-invalid="validationErrorMessages !== null"
                    filter-categories
                    class="failed-purchase-product-type"
                    :placeholder="$t('funnel.edit.sideBar.goal.failedPurchase.selectProduct')"
                    :options="products"
                    data-qa="failed-purchase-product"
                >
                    <template #help>
                        <span v-if="validationErrorMessages !== null">
                            {{ validationErrorMessages }}
                        </span>
                    </template>
                </CustomMultiselect>
                <DsMultiselect
                    v-if="isCategory"
                    v-model="selectedCategory"
                    :force-invalid="validationErrorMessages !== null"
                    class="failed-purchase-category-type"
                    bind-value-only
                    data-qa="failed-purchase-category"
                    searchable
                    label-prop="label"
                    value-prop="value"
                    :placeholder="$t('funnel.edit.sideBar.goal.failedPurchase.selectCategory')"
                    :options="categories"
                >
                    <template #help>
                        <span v-if="validationErrorMessages !== null">
                            {{ validationErrorMessages }}
                        </span>
                    </template>
                </DsMultiselect>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import GoalModal from '../goalModal/GoalModal.vue';
import {
    loadProductList,
    loadProductCategories,
} from '../productPurchased/purchase.api';
import { utils } from '../../../utils/legacy-utils';
import CustomMultiselect from '@/components/shared/CustomMultiselect.vue';

export const PURCHASE_TYPE_IDS = {
    product: 'product',
    category: 'category',
    all: 'all',
};

export default {

    components: {
        GoalModal,
        CustomMultiselect,
    },
    PURCHASE_TYPE_IDS,

    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    failedPurchaseId: null,
                    failedPurchaseOption: null,
                    eventId: null,
                },
            }),
        },
        appInfo: {
            type: Object,
            required: true,
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            selectedFailedPurchaseType: this.cell?.value?.failedPurchaseType ?? PURCHASE_TYPE_IDS.product,
            failedPurchaseType: this.cell?.value?.failedPurchaseType,
            failedPurchaseTypes: [
                {
                    value: PURCHASE_TYPE_IDS.product,
                    label: this.$t('funnel.edit.sideBar.goal.failedPurchase.specificProduct'),
                    helpText: this.$t('funnel.edit.sideBar.goal.failedPurchase.specificProduct.help'),
                }, {
                    value: PURCHASE_TYPE_IDS.category,
                    label: this.$t('funnel.edit.sideBar.goal.failedPurchase.specificCategory'),
                    helpText: this.$t('funnel.edit.sideBar.goal.failedPurchase.specificCategory.help'),
                }, {
                    value: PURCHASE_TYPE_IDS.all,
                    label: this.$t('funnel.edit.sideBar.goal.failedPurchase.anyPurchase'),
                    helpText: this.$t('funnel.edit.sideBar.goal.failedPurchase.anyPurchase.help'),
                },
            ],
            products: [],
            categories: [],
            selectedProduct: null,
            selectedCategory: null,
            purchaseOptions: [],
            selectedPurchaseOption: null,
            validationErrorMessages: null,
        };
    },

    computed: {
        isProduct() {
            return this.selectedFailedPurchaseType === this.$options.PURCHASE_TYPE_IDS.product;
        },
        isCategory() {
            return this.selectedFailedPurchaseType === this.$options.PURCHASE_TYPE_IDS.category;
        },
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },
        toolTipLink() {
            return this.isSpaApp ? this.$t('funnel.edit.sideBar.goal.failedPurchase.keap') : this.$t('funnel.edit.sideBar.goal.failedPurchase.classic');
        },
    },

    async mounted() {
        await this.load();
    },

    methods: {
        async load() {
            try {
                await this.loadDropdowns();
                this.loadCellValue();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`Could not load products: ${error}`);
            }
        },
        async loadDropdowns() {
            this.products = await loadProductList();
            this.categories = await loadProductCategories();
        },
        loadCellValue() {
            this.selectedFailedPurchaseType = this.cell?.value?.failedPurchaseType ?? PURCHASE_TYPE_IDS.product;

            if (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.product) {
                const harmonizedFailedPurchaseId = String(utils.removeLongSuffix(this.cell.value?.failedPurchaseId));

                if (this.hasGroups(harmonizedFailedPurchaseId)) {
                    this.selectedProduct = `${harmonizedFailedPurchaseId}:${String(utils.removeLongSuffix(this.cell?.value?.failedPurchaseOption))}`;
                } else {
                    this.selectedProduct = harmonizedFailedPurchaseId;
                }
            } else if (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.category) {
                this.selectedCategory = Number(utils.removeLongSuffix(this.cell.value?.failedPurchaseId));
            }
        },

        hasGroups(purchaseId) {
            const options = this.products.filter((product) => product.category.value === purchaseId && product.groups);

            return options.length > 0;
        },

        closeModal() {
            this.$emit('close-modal');
        },

        isReady() {
            const valid = (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.all) || ((this.isProduct && this.selectedProduct && this.selectedProduct !== 'undefined') || (this.isCategory && this.selectedCategory && this.selectedCategory !== 'undefined'));

            if (!valid) {
                this.validationErrorMessages = this.$t('funnel.edit.sideBar.goal.failedPurchase.error.productEmpty');
            }

            return valid;
        },

        updateConfig() {
            if (this.isReady()) {
                if (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.all) {
                    this.selectedProduct = 0;
                    this.failedPurchaseOption = '0';
                } else if (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.product) {
                    const [product, option] = this.selectedProduct.split(':');

                    if (option) {
                        this.selectedProduct = product;
                        this.failedPurchaseOption = option;
                    } else {
                        this.selectedProduct = product;
                        this.failedPurchaseOption = '0';
                    }
                } else if (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.category) {
                    this.selectedProduct = this.selectedCategory;
                    this.failedPurchaseOption = '0';
                }

                const cell = { ...this.cell };

                cell.value = cell.value || {};

                cell.value.failedPurchaseType = this.selectedFailedPurchaseType;
                cell.value.failedPurchaseId = this.selectedProduct;
                cell.value.failedPurchaseOption = this.failedPurchaseOption;

                cell.value.eventId = this.failedPurchaseOption && this.failedPurchaseOption !== '0'
                    ? this.failedPurchaseOption
                    : this.selectedProduct;

                this.$emit('update-cell-value', { cell });
            }
        },

        selectPaymentOption() {
            if (!this.isProduct) {
                this.selectedProduct = null;
            }

            if (!this.isCategory) {
                this.selectedCategory = null;
            }

            this.loadDropdowns();
        },
    },
};
</script>


<style lang="scss">
.selected-purchase-type .multiselect-single label {
    padding: 0;
}
.menu-item .option-subtext {
    font-size: $font-size-xs;
    color: $color-ink-800;
}

.menu-item.selected .option-subtext {
    color: $color-paper;
}
</style>
